import Vue from 'vue'
import VueRouter from 'vue-router'
import { validateAuthRoute } from '@/velta/components/auth'
import routesEmpresa from './companyRoutes.js'
import routesCliente from './clientRoutes.js'
import routesSite from './siteRoutes.js'
import useAppConfig from '@core/app-config/useAppConfig'
import useDailyAccessLogger from '@/composables/useDailyAccessLogger'
import { gravarUltimaRota } from '@/velta/utils/ultimaRota'
import {
    iniciarProgresso,
    marcarFimDoGuard,
    marcarFimDoChunk,
    concluirProgresso,
} from '@/libs/navegacao/progressoNavegacao'

Vue.use(VueRouter)

// O Vue Router 3 rejeita a Promise de push/replace quando a navegação é
// redundante (NavigationDuplicated) ou abortada/redirecionada por um guard
// (Redirected). São casos benignos, mas sem `.catch()` viram "unhandledrejection"
// e poluem o Sentry. Fazemos o override global para ignorar apenas essas falhas
// de navegação, repassando qualquer erro real. (Sentry VELTACAR-VUE-3/E/N)
const isBenignNavigationError = (err) => {
    if (!err) return false
    if (err._isRouter) return true
    if (err.name === 'NavigationDuplicated') return true
    const message = typeof err.message === 'string' ? err.message : ''
    return (
        message.includes('Avoided redundant navigation') ||
        message.includes('Redirected when going from') ||
        message.includes('navigation guard')
    )
}

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalPush.call(this, location, onResolve, onReject)
    }
    return originalPush.call(this, location).catch((err) => {
        if (isBenignNavigationError(err)) return err
        return Promise.reject(err)
    })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalReplace.call(this, location, onResolve, onReject)
    }
    return originalReplace.call(this, location).catch((err) => {
        if (isBenignNavigationError(err)) return err
        return Promise.reject(err)
    })
}

const router = new VueRouter({
    mode: 'history',
    scrollBehavior() {
        return {
            x: 0,
            y: 0,
        }
    },
    routes: [
        {
            path: '/catalogo-virtual/:slug',
            name: 'catalogo-virtual',
            component: () => import('@/views/CatalogoVirtualCompany.vue'),
            meta: {
                pageTitle: 'Catálogo Virtual',
                layout: 'catalogo-virtual'
            },
        },
        {
            path: '/catalogo-virtual/:slug/agendamento',
            name: 'catalogo-virtual-agendamento',
            component: () => import('@/views/CatalogoVirtualAgendamento.vue'),
            meta: {
                pageTitle: 'Agendamento - Catálogo Virtual',
                layout: 'catalogo-virtual'
            },
        },
        ...routesEmpresa,
        ...routesCliente,
        ...routesSite,
    ]
})

// Hooks de navegação rodam na ordem em que são registrados. Este vem ANTES do
// guard de autenticação de propósito: o cronômetro (e a barra de progresso)
// precisam começar junto com o clique do usuário, não depois das checagens de
// sessão e permissão — que são justamente uma das fases que queremos medir.
router.beforeEach((to, from, next) => {
    iniciarProgresso(to, from)
    return next()
})

router.beforeEach((to, from, next) => {
    const { skin } = useAppConfig()

    const isHomeSite = ['site-home', 'site-home-lavarapido', 'site-home-lavajato', 'site-home-esteticaautomotiva', 'site-home-mecanica', 'site-home-chapeacao', 'site-home-funilaria', 'site-home-borracharia'].includes(to.name)

    if (skin.value === 'dark' && !isHomeSite) {
        document.body.classList.add('dark-layout')
    } else {
        document.body.classList.remove('dark-layout')
    }

    Vue.nextTick(() => {
        let description = 'VeltaCar é um sistema de gestão para empresas do ramo automotivo. Lava-jatos, estéticas automotivas, oficinas mecânicas, chapeação, funilarias e borracharias. Emissão de nota fiscal, agendamentos, orçamentos e mais.'
        let keywords = 'velta, veltacar, sistema, gestão, software, plataforma, ramo automotivo, lava-jato, lava-rápido, estética automotiva, mecânica, chapeação, funilaria, borracharia, nota fiscal, agendamentos'
        let title = 'VeltaCar - Sistema de Gestão Automotiva'

        if (isHomeSite) {
            const entity = to.meta?.entity ?? "Automotiva"
            if (entity !== 'Automotiva') {
                title = `Sistema de Gestão para ${entity} - VeltaCar`
                description = `VeltaCar é um sistema para gestão para ${entity}. O sistema realiza emissão de nota fiscal, agendamentos, cadastro de clientes, orçamentos e mais.`
                keywords = `${entity}, velta, veltacar, sistema, software, plataforma, gestão, nota fiscal, agendamentos`
            }
        } else {
            title = (!!to.meta.pageTitle ? to.meta.pageTitle + ' | ' : '') + `VeltaCar - Sistema de Gestão Automotiva`
        }

        let metaDescriptionTag = document.querySelector('meta[name="description"]');
        let metaKeywordsTag = document.querySelector('meta[name="keywords"]');

        if (metaDescriptionTag) {
            metaDescriptionTag.content = description;
        }

        if (metaKeywordsTag) {
            metaKeywordsTag.content = keywords;
        }
        document.title = title
    });


    // Inicializa o registro de acesso diário
    const { initDailyAccessLogger } = useDailyAccessLogger()
    initDailyAccessLogger()


    // `next(false)` aborta a navegação sem disparar `afterEach` nem `onError` —
    // o guard usa isso nos dois caminhos de bloqueio por plano, que exibem um
    // swal e deixam o usuário onde estava. Sem interceptar aqui, a barra ficaria
    // girando até o watchdog de 20s.
    return validateAuthRoute(to, from, (...argumentos) => {
        if (argumentos[0] === false) concluirProgresso('abortada')

        return next(...argumentos)
    })
})

// Registrado DEPOIS do guard: só chega aqui quando `validateAuthRoute` liberou a
// navegação sem redirecionar, o que torna este ponto o fim exato da fase "guard".
router.beforeEach((to, from, next) => {
    marcarFimDoGuard()
    return next()
})

// `beforeResolve` roda depois de o vue-router resolver os componentes assíncronos
// da rota: é a fronteira entre "esperando o download do chunk" e "montando a tela".
router.beforeResolve((to, from, next) => {
    marcarFimDoChunk()
    return next()
})

// Falha ao carregar o chunk da tela (rede caiu, asset de deploy antigo). O
// `main.js` cuida do recarregamento; aqui só não podemos deixar a barra presa.
router.onError(() => {
    concluirProgresso('erro')
})

router.afterEach((to) => {
    concluirProgresso('fim')

    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = 'none'
    }

    gravarUltimaRota(to)
})

export default router
